import { useCallback, useRef, useEffect } from 'react';
import { idbService, tokenService } from 'utils/services';

// Drafts untouched for longer than this are considered stale and pruned on load.
// Exported so the drafts reminder shares one definition of "stale" rather than
// offering a link to a draft this page would delete the moment it opened.
export const SURVEY_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = SURVEY_DRAFT_MAX_AGE_MS;
// Debounce window for autosave-on-typing so we don't hit IDB on every keystroke.
const SAVE_DEBOUNCE_MS = 500;

// A draft is scoped to the exact form/retailer/assignment a given user is
// filling, so the same survey URL after re-login resolves to the same draft
// while a different user on the same device never sees it.
const buildKey = ({ userId, formId, retailerId, assignmentId }) => {
  if (!userId || !formId || !retailerId) return null;
  return `${userId}:${formId}:${retailerId}:${assignmentId || 'none'}`;
};

// Builds the persisted payload from the live survey model. File-upload answers
// are dropped on purpose: their in-progress blob/object-URL state can't survive
// a reload, so persisting them would be both meaningless and large. The auth
// token is recorded so the page can tell a same-session reload (token unchanged)
// from a return after re-login (new token).
const buildPayload = (survey, { reachedSubmission, formName, retailerName }) => {
  const surveyData = { ...(survey.data || {}) };
  survey.getAllQuestions().forEach((question) => {
    if (question.getType() === 'file') {
      delete surveyData[question.name];
    }
  });
  return {
    surveyData,
    currentPageNo: survey.currentPageNo,
    reachedSubmission: !!reachedSubmission,
    formName: formName || '',
    // Stored so a list of drafts can name the store without an API round-trip per
    // row. Drafts written before this field existed fall back to the retailer id.
    retailerName: retailerName || '',
    token: tokenService.getToken(),
    updatedAt: new Date().toISOString(),
  };
};

// Autosaves the in-progress CreateSurvey form to IndexedDB and restores it on
// return. All IDB access is best-effort — a persistence failure must never
// block someone from filling or submitting the form.
const useSurveyDraft = ({ userId, formId, retailerId, assignmentId }) => {
  const key = buildKey({ userId, formId, retailerId, assignmentId });
  const timerRef = useRef(null);
  // Holds the most recent (survey, meta) queued by the debounced save so a flush
  // can persist it immediately instead of waiting out the debounce window.
  const pendingRef = useRef(null);

  const saveDraftNow = useCallback(
    async (survey, meta = {}) => {
      if (!key || !survey) return;
      pendingRef.current = null;
      try {
        await idbService.putSurveyDraft(key, buildPayload(survey, meta));
      } catch {
        // Best-effort autosave: a save failure must never interrupt form filling.
      }
    },
    [key],
  );

  const saveDraft = useCallback(
    (survey, meta = {}) => {
      if (!key || !survey) return;
      pendingRef.current = { survey, meta };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => saveDraftNow(survey, meta), SAVE_DEBOUNCE_MS);
    },
    [key, saveDraftNow],
  );

  // Persists any edit still waiting out the debounce window. Used on unmount so
  // the last keystrokes before an in-app navigation aren't dropped.
  const flushDraft = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const pending = pendingRef.current;
    if (pending) saveDraftNow(pending.survey, pending.meta);
  }, [saveDraftNow]);

  useEffect(() => {
    return () => flushDraft();
  }, [flushDraft]);

  const loadDraft = useCallback(async () => {
    const empty = { draft: null, isSameSession: false, hasContent: false };
    if (!key) return empty;
    try {
      await idbService.pruneExpiredSurveyDrafts(SEVEN_DAYS_MS);
      const draft = await idbService.getSurveyDraft(key);
      if (!draft) return empty;
      const hasContent =
        Object.keys(draft.surveyData || {}).length > 0 || draft.currentPageNo > 0 || !!draft.reachedSubmission;
      const isSameSession = !!draft.token && draft.token === tokenService.getToken();
      return { draft, isSameSession, hasContent };
    } catch {
      // Best-effort restore: if IDB is unavailable, fall through to a fresh form.
      return empty;
    }
  }, [key]);

  const clearDraft = useCallback(async () => {
    if (!key) return;
    // Cancel any pending debounced save so it can't re-create the draft we clear.
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = null;
    try {
      await idbService.removeSurveyDraft(key);
    } catch {
      // Best-effort cleanup: a stale draft will be pruned by the 7-day sweep on next load.
    }
  }, [key]);

  return { saveDraft, saveDraftNow, flushDraft, loadDraft, clearDraft };
};

export default useSurveyDraft;
